import { useEffect, useRef, useState } from 'react';

import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';

export async function fetchWelcomeDismissed(): Promise<boolean> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/welcome-banner`, { headers: authHeaders(token) });
  if (!response.ok) {
    throw new Error(`welcome-banner fetch failed: ${response.status}`);
  }
  const body = (await response.json()) as { dismissed?: unknown } | null;
  if (typeof body?.dismissed !== 'boolean') {
    throw new Error('welcome-banner response is missing dismissed');
  }
  return body.dismissed;
}

export async function dismissWelcome(): Promise<void> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/welcome-banner/dismiss`, {
    method: 'POST',
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`welcome-banner dismiss failed: ${response.status}`);
  }
}

export type WelcomeSource = {
  dismissed: () => Promise<boolean>;
  dismiss: () => Promise<void>;
};

export const accountWelcome: WelcomeSource = {
  dismissed: fetchWelcomeDismissed,
  dismiss: dismissWelcome,
};

export function useWelcomeBanner(
  source: WelcomeSource,
  { enabled, persist }: { enabled: boolean; persist: boolean },
): { visible: boolean; dismiss: () => void } {
  const [visible, setVisible] = useState(false);
  const sourceRef = useRef(source);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    sourceRef.current
      .dismissed()
      .then((dismissed) => {
        if (active) setVisible(!dismissed);
      })
      .catch((err) => {
        console.warn('welcome banner state unavailable; showing no banner', err);
      });
    return () => {
      active = false;
    };
  }, [enabled]);

  const dismiss = () => {
    setVisible(false);
    if (!persist) return;
    sourceRef.current.dismiss().catch((err) => {
      console.warn('welcome banner dismissal was not recorded', err);
    });
  };

  return { visible: enabled && visible, dismiss };
}
